import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import axiosInstance from '../../../axios/axiosInstance';
import apiEndpoints from '../../../axios/api';
import usePermissions from '../../../hook/usePermissions';
import { PERMISSIONS } from '../../../consts/permissions';
import { getProjectsList } from '../../../redux/actions/SocietyPortal/TaskAction/TaskAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { getVendorsDropdown } from '../../../redux/actions/SocietyPortal/VendorAction/VendorAction';
import { getEmployeesDropdown } from '../../../redux/actions/SocietyPortal/EmployeeAction/EmployeeAction';
import SearchableDropdown from '../../../components/SearchableDropdown';
import { selectedSocietyStorer } from '../../../utils/webStorer';
import { TASK_PRIORITIES, TASK_DEPARTMENTS, humanize } from '../../../consts/taskConstants';

// Turn an ISO timestamp into a value for <input type="datetime-local">.
const toLocalInput = (value) => {
    if (!value) return '';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const EMPTY = {
    title: '', description: '', priority: 'MEDIUM', category: '', department: '',
    project_id: '', assigned_to: '',
    start_date: '', due_date: '', sla_due_at: '', estimated_minutes: '',
    progress_percent: '', actual_minutes: '',
    flat_id: '', wing_id: '', vendor_id: '',
};

// Linear create-task flow — one section per step.
const STEPS = [
    { key: 'details', title: 'Details', icon: 'mdi:text-box-outline' },
    { key: 'schedule', title: 'Schedule', icon: 'mdi:calendar-clock' },
    { key: 'assign', title: 'Assignment', icon: 'mdi:account-check-outline' },
    { key: 'links', title: 'Links', icon: 'mdi:link-variant' },
];

const TaskForm = ({ mode = 'add', initialValues, submitting, onSubmit }) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();
    const { projectsListData } = useSelector((s) => s.TaskReducer);
    const { wingsDropdown, wingsDropdownLoading, flatsDropdown, flatsDropdownLoading } = useSelector((s) => s.WingsAndFlatsReducers);
    const { vendorsDropdownData, vendorsDropdownLoading } = useSelector((s) => s.VendorReducers);
    const { employeesDropdownData, employeesDropdownLoading } = useSelector((s) => s.EmployeeReducer);

    const { can } = usePermissions();
    const canSeeManagers = can(PERMISSIONS.COMMITTEE_OVERSIGHT);
    // Opened from Committee Oversight with a manager picked: start assigned to them.
    const [searchParams] = useSearchParams();
    const presetAssignee = mode === 'add' ? searchParams.get('assigned_to') : null;

    const [form, setForm] = useState({
        ...EMPTY,
        ...(presetAssignee ? { assigned_to: presetAssignee } : {}),
        ...(initialValues || {}),
    });
    // The society's managers (Society Manager role or Manager employees), so the
    // Secretary and other committee roles can assign them work.
    const [managers, setManagers] = useState([]);
    const [attempted, setAttempted] = useState(false);
    const [step, setStep] = useState(0);

    const isWizard = mode === 'add';
    const isLast = step === STEPS.length - 1;

    useEffect(() => {
        dispatch(getProjectsList({}));
        if (societyId) {
            dispatch(getWingsDropdown({ society_id: societyId }));
            dispatch(getFlatsDropdown({ society_id: societyId }));
            dispatch(getVendorsDropdown({ societyId }));
            dispatch(getEmployeesDropdown({ societyId }));
        }
    }, [dispatch, societyId]);

    useEffect(() => {
        if (!societyId || !canSeeManagers) return;
        axiosInstance.get(apiEndpoints.COMMITTEE_MANAGERS_API, { params: { society_id: societyId } })
            .then((res) => setManagers(Array.isArray(res?.data) ? res.data : []))
            .catch(() => setManagers([]));
    }, [societyId, canSeeManagers]);

    useEffect(() => {
        if (initialValues) {
            setForm({
                ...EMPTY,
                ...initialValues,
                start_date: toLocalInput(initialValues.start_date),
                due_date: toLocalInput(initialValues.due_date),
                sla_due_at: toLocalInput(initialValues.sla_due_at),
            });
        }
    }, [initialValues]);

    const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));
    // SearchableDropdown emits 'all' when its clear button is pressed → treat as empty.
    const setSelect = (key, value) => set(key, value === 'all' ? '' : value);

    const priorityOptions = useMemo(() => TASK_PRIORITIES.map((p) => ({ value: p, label: humanize(p) })), []);
    const departmentOptions = useMemo(() => TASK_DEPARTMENTS.map((d) => ({ value: d, label: humanize(d) })), []);
    const projectOptions = useMemo(() => (Array.isArray(projectsListData) ? projectsListData : []).map((p) => ({ value: String(p.project_id), label: p.name })), [projectsListData]);
    const wingOptions = useMemo(() => (Array.isArray(wingsDropdown) ? wingsDropdown : []).map((w) => ({ value: String(w.wing_id), label: w.title })), [wingsDropdown]);
    const flatOptions = useMemo(() => (Array.isArray(flatsDropdown) ? flatsDropdown : []).map((f) => ({ value: String(f.flat_id), label: f.flat_number })), [flatsDropdown]);
    const vendorOptions = useMemo(() => (Array.isArray(vendorsDropdownData) ? vendorsDropdownData : []).map((v) => ({ value: String(v.vendor_id), label: v.business_name })), [vendorsDropdownData]);
    // Assign-to options — society managers first (a Society Manager login need not
    // be an employee), then employees with a linked user account. assigned_to is a user_id.
    const employeeOptions = useMemo(() => {
        const managerIds = new Set(managers.map((m) => String(m.user_id)));
        const managerOptions = managers.map((m) => ({ value: String(m.user_id), label: `${m.name} — Society Manager` }));
        const others = (Array.isArray(employeesDropdownData) ? employeesDropdownData : [])
            .filter((e) => e.user_id != null && !managerIds.has(String(e.user_id)))
            .map((e) => {
                const name = `${e.first_name || ''} ${e.last_name || ''}`.trim();
                return { value: String(e.user_id), label: e.employee_type ? `${name} — ${e.employee_type}` : name };
            });
        return [...managerOptions, ...others];
    }, [employeesDropdownData, managers]);

    const toIso = (local) => (local ? new Date(local).toISOString() : undefined);

    // Step 0 holds the only required field, the title.
    const detailsValid = () => Boolean(form.title.trim());
    const goNext = () => {
        if (step === 0 && !detailsValid()) { setAttempted(true); return; }
        setStep((s) => Math.min(s + 1, STEPS.length - 1));
    };
    const goBack = () => setStep((s) => Math.max(s - 1, 0));

    const handleSubmit = (e) => {
        e.preventDefault();
        // In the wizard, Enter / the primary button advances until the last step.
        if (isWizard && !isLast) { goNext(); return; }
        setAttempted(true);
        if (!form.title.trim()) { if (isWizard) setStep(0); return; }

        const num = (v) => {
            if (v === '' || v === null || v === undefined) return undefined;
            const n = Number(v);
            return Number.isNaN(n) ? undefined : n;
        };

        const payload = {
            title: form.title.trim(),
            description: form.description || undefined,
            priority: form.priority || undefined,
            category: form.category || undefined,
            department: form.department || undefined,
            project_id: num(form.project_id),
            start_date: toIso(form.start_date),
            due_date: toIso(form.due_date),
            sla_due_at: toIso(form.sla_due_at),
            estimated_minutes: num(form.estimated_minutes),
            flat_id: num(form.flat_id),
            wing_id: num(form.wing_id),
            vendor_id: num(form.vendor_id),
        };

        if (mode === 'add') {
            const assignedTo = num(form.assigned_to);
            if (assignedTo) payload.assigned_to = assignedTo;
        } else {
            // Sent as null so clearing the project is saved.
            payload.project_id = num(form.project_id) ?? null;
            payload.progress_percent = num(form.progress_percent);
            payload.actual_minutes = num(form.actual_minutes);
            if (initialValues?.version !== undefined) payload.version = initialValues.version;
        }

        // Drop undefined keys so we never overwrite with null.
        Object.keys(payload).forEach((k) => payload[k] === undefined && delete payload[k]);
        onSubmit(payload);
    };

    const inputCls = 'tw-w-full tw-px-2.5 tw-py-1.5 tw-border tw-border-gray-200 tw-rounded-md tw-text-sm focus:tw-outline-none focus:tw-border-blue-500 tw-bg-white';
    const labelCls = 'tw-block tw-text-xs tw-font-medium tw-text-gray-600 tw-mb-1';
    // In the wizard each step shows one section, so drop the between-section divider.
    const stepTop = isWizard ? '' : 'tw-border-t tw-border-gray-100 tw-pt-4';

    return (
        <form onSubmit={handleSubmit}>
            <div className="tw-flex tw-items-center tw-justify-between tw-gap-3 tw-mb-4">
                <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
                    <button type="button" onClick={() => navigate(-1)} className="tw-p-1.5 tw-rounded hover:tw-bg-gray-100 tw-text-gray-500">
                        <Icon icon="mdi:arrow-left" />
                    </button>
                    <div className="tw-min-w-0">
                        <h1 className="tw-m-0 tw-text-lg tw-font-semibold tw-text-gray-800 tw-truncate">{mode === 'add' ? 'New Task' : 'Edit Task'}</h1>
                        <p className="tw-m-0 tw-text-xs tw-text-gray-500 tw-truncate">{mode === 'add' ? 'Create a task and optionally assign it' : 'Update task details'}</p>
                    </div>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0">
                    <button type="button" onClick={() => navigate(-1)} disabled={submitting}
                        className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-50">
                        Cancel
                    </button>
                    {!isWizard && (
                        <button type="submit" disabled={submitting}
                            className="tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg tw-flex tw-items-center tw-gap-2 disabled:tw-opacity-50">
                            {submitting
                                ? <><Icon icon="mdi:loading" className="tw-animate-spin" /> Saving...</>
                                : <><Icon icon="mdi:content-save-outline" /> Save Changes</>}
                        </button>
                    )}
                </div>
            </div>

            <div className="tw-bg-white tw-rounded-lg tw-shadow-sm tw-border tw-border-gray-100">
                {/* Stepper (create flow) */}
                {isWizard && (
                    <div className="tw-flex tw-items-center tw-px-5 tw-py-4 tw-border-b tw-border-gray-100 tw-overflow-x-auto">
                        {STEPS.map((s, i) => (
                            <div key={s.key} className="tw-flex tw-items-center tw-flex-1 last:tw-flex-none last:tw-grow-0">
                                <button type="button" onClick={() => i < step && setStep(i)}
                                    className={`tw-flex tw-items-center tw-gap-2 tw-flex-shrink-0 ${i < step ? 'tw-cursor-pointer' : 'tw-cursor-default'}`}>
                                    <span className={`tw-w-7 tw-h-7 tw-rounded-full tw-flex tw-items-center tw-justify-center tw-text-xs tw-font-semibold tw-transition-colors ${
                                        i <= step ? 'tw-bg-blue-600 tw-text-white' : 'tw-bg-gray-100 tw-text-gray-400'
                                    }`}>
                                        {i < step ? <Icon icon="mdi:check" /> : i + 1}
                                    </span>
                                    <span className={`tw-text-sm tw-font-medium tw-whitespace-nowrap ${i <= step ? 'tw-text-gray-800' : 'tw-text-gray-400'} tw-hidden sm:tw-inline`}>{s.title}</span>
                                </button>
                                {i < STEPS.length - 1 && <div className={`tw-flex-1 tw-h-0.5 tw-mx-3 tw-rounded ${i < step ? 'tw-bg-blue-500' : 'tw-bg-gray-200'}`} />}
                            </div>
                        ))}
                    </div>
                )}

                <div className="tw-p-5 tw-space-y-5">
                {/* Basics */}
                {(!isWizard || step === 0) && (
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                    <div className="md:tw-col-span-2">
                        <label className={labelCls}>Project</label>
                        <SearchableDropdown value={form.project_id} onChange={(v) => setSelect('project_id', v)}
                            options={projectOptions} placeholder="— Select project —" />
                    </div>
                    <div className="md:tw-col-span-2">
                        <label className={labelCls}>Title <span className="tw-text-red-500">*</span></label>
                        <input type="text" maxLength={200} value={form.title} onChange={(e) => set('title', e.target.value)}
                            placeholder="e.g. Fix lobby light" className={inputCls} />
                        {attempted && !form.title.trim() && <p className="tw-text-[11px] tw-text-red-500 tw-mt-1">Title is required</p>}
                    </div>
                    <div className="md:tw-col-span-2">
                        <label className={labelCls}>Description</label>
                        <textarea rows={3} value={form.description} onChange={(e) => set('description', e.target.value)}
                            placeholder="Add more detail..." className={inputCls} />
                    </div>
                    <div>
                        <label className={labelCls}>Priority</label>
                        <SearchableDropdown value={form.priority} onChange={(v) => setSelect('priority', v)}
                            options={priorityOptions} placeholder="Select priority" clearable={false} />
                    </div>
                    <div>
                        <label className={labelCls}>Department</label>
                        <SearchableDropdown value={form.department} onChange={(v) => setSelect('department', v)}
                            options={departmentOptions} placeholder="Select department" />
                    </div>
                    <div>
                        <label className={labelCls}>Category</label>
                        <input type="text" value={form.category} onChange={(e) => set('category', e.target.value)}
                            placeholder="e.g. Electrical" className={inputCls} />
                    </div>
                </div>
                )}

                {/* Scheduling */}
                {(!isWizard || step === 1) && (
                <div className={stepTop}>
                    <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-mb-3">Scheduling</p>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                        <div>
                            <label className={labelCls}>Start Date</label>
                            <input type="datetime-local" value={form.start_date} onChange={(e) => set('start_date', e.target.value)} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Due Date</label>
                            <input type="datetime-local" value={form.due_date} onChange={(e) => set('due_date', e.target.value)} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>SLA Due</label>
                            <input type="datetime-local" value={form.sla_due_at} onChange={(e) => set('sla_due_at', e.target.value)} className={inputCls} />
                        </div>
                        <div>
                            <label className={labelCls}>Estimated (min)</label>
                            <input type="number" min="0" value={form.estimated_minutes} onChange={(e) => set('estimated_minutes', e.target.value)} className={inputCls} />
                        </div>
                    </div>
                </div>
                )}

                {mode === 'edit' && (
                    <div className="tw-border-t tw-border-gray-100 tw-pt-4">
                        <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-mb-3">Progress</p>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-4 tw-gap-4">
                            <div>
                                <label className={labelCls}>Progress %</label>
                                <input type="number" min="0" max="100" value={form.progress_percent} onChange={(e) => set('progress_percent', e.target.value)} className={inputCls} />
                            </div>
                            <div>
                                <label className={labelCls}>Actual (min)</label>
                                <input type="number" min="0" value={form.actual_minutes} onChange={(e) => set('actual_minutes', e.target.value)} className={inputCls} />
                            </div>
                        </div>
                        <p className="tw-text-[11px] tw-text-gray-400 tw-mt-2">Status changes are made from the task detail page.</p>
                    </div>
                )}

                {isWizard && step === 2 && (
                    <div>
                        <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-mb-3">Assignment</p>
                        <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                            <div>
                                <label className={labelCls}>Assign to (employee)</label>
                                <SearchableDropdown value={form.assigned_to} onChange={(v) => setSelect('assigned_to', v)}
                                    options={employeeOptions} loading={employeesDropdownLoading} placeholder={managers.length ? 'Select manager or employee' : 'Select employee'} />
                            </div>
                        </div>
                        <p className="tw-text-[11px] tw-text-gray-400 tw-mt-2">Assigning an employee starts the task as ASSIGNED, otherwise PENDING.</p>
                    </div>
                )}

                {/* Linked entities */}
                {(!isWizard || step === 3) && (
                <div className={stepTop}>
                    <p className="tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-mb-3">Linked Entities <span className="tw-normal-case tw-font-normal tw-text-gray-400">(optional)</span></p>
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                        <div>
                            <label className={labelCls}>Wing</label>
                            <SearchableDropdown value={form.wing_id} onChange={(v) => setSelect('wing_id', v)}
                                options={wingOptions} loading={wingsDropdownLoading} placeholder="Select wing" />
                        </div>
                        <div>
                            <label className={labelCls}>Flat</label>
                            <SearchableDropdown value={form.flat_id} onChange={(v) => setSelect('flat_id', v)}
                                options={flatOptions} loading={flatsDropdownLoading} placeholder="Select flat" />
                        </div>
                        <div>
                            <label className={labelCls}>Vendor</label>
                            <SearchableDropdown value={form.vendor_id} onChange={(v) => setSelect('vendor_id', v)}
                                options={vendorOptions} loading={vendorsDropdownLoading} placeholder="Select vendor" />
                        </div>
                    </div>
                </div>
                )}
                </div>

                {/* Wizard navigation */}
                {isWizard && (
                    <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-4 tw-border-t tw-border-gray-100">
                        <button type="button" onClick={goBack} disabled={step === 0}
                            className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-600 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-lg disabled:tw-opacity-40 disabled:tw-cursor-not-allowed">
                            <Icon icon="mdi:arrow-left" /> Back
                        </button>
                        <div className="tw-flex tw-items-center tw-gap-3">
                            <span className="tw-text-xs tw-text-gray-400">Step {step + 1} of {STEPS.length}</span>
                            {/* Distinct keys so React swaps the node instead of flipping Next's type to
                                submit mid-click, which would submit the form on arriving at the last step. */}
                            {isLast ? (
                                <button key="create" type="submit" disabled={submitting}
                                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg disabled:tw-opacity-50">
                                    {submitting ? <><Icon icon="mdi:loading" className="tw-animate-spin" /> Saving...</> : <><Icon icon="mdi:check" /> Create Task</>}
                                </button>
                            ) : (
                                <button key="next" type="button" onClick={goNext}
                                    className="tw-flex tw-items-center tw-gap-1.5 tw-px-4 tw-py-2 tw-text-sm tw-font-medium tw-text-white tw-bg-blue-600 hover:tw-bg-blue-700 tw-rounded-lg">
                                    Next <Icon icon="mdi:arrow-right" />
                                </button>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </form>
    );
};

export default TaskForm;
